import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Breadcrumb, Card, Col, Row, Badge, Spinner, Form, Button, Alert } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import {
    applyOptimisticPinToTasks,
    formatGanttDateTime,
    formatTimelineScopeRange,
    formatTaskContext,
    isActiveTeamMember,
} from './ganttHelpers';
import TeamStyleGanttChart, {
    useTeamStyleTimeline,
} from '../../components/TeamStyleGanttChart';
import { ViewMode } from 'gantt-task-react';
import { groupTasksByAssignee } from '../../utils/mapToGanttTasks';
import ScheduleConflictModal from '../../components/ScheduleConflictModal';
import '../../assets/css/user-gantt.css';
import '../../assets/css/team-planner.css';
import '../../assets/css/timeline-ui.css';

const isCompletedListName = (name) => {
    const n = String(name || '').toLowerCase().replace(/[\s_-]+/g, '');
    return n === 'completed' || n === 'done';
};

const boardsToProjects = (boards) => {
    const map = new Map();
    for (const board of boards) {
        if (!board.project_id) continue;
        const pid = board.project_id;
        if (!map.has(pid)) {
            map.set(pid, {
                project_id: pid,
                project_name: board.project?.project_name || `Project #${pid}`,
                brand_name: board.brand?.brand_name || null,
                board_count: 0,
                active_card_count: 0,
                boards: [],
            });
        }
        const entry = map.get(pid);
        entry.board_count += 1;
        entry.boards.push({ board_id: board.board_id, name: board.name });
        for (const list of board.lists || []) {
            if (isCompletedListName(list.name)) continue;
            entry.active_card_count += list.card_count ?? list.cards?.length ?? 0;
        }
    }
    return Array.from(map.values()).sort((a, b) =>
        a.project_name.localeCompare(b.project_name)
    );
};

const AdminGantt = () => {
    const { accessToken, userId } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [projectId, setProjectId] = useState(searchParams.get('project_id') || '');
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    const [projectName, setProjectName] = useState('');
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [previewUserId, setPreviewUserId] = useState('');
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [applyLoading, setApplyLoading] = useState(false);
    const [applyMessage, setApplyMessage] = useState('');
    const [conflicts, setConflicts] = useState([]);
    const [priorityWarnings, setPriorityWarnings] = useState([]);
    const [conflictsLoading, setConflictsLoading] = useState(false);
    const [pinSaving, setPinSaving] = useState(false);
    const [pinMessage, setPinMessage] = useState('');
    const skipNextGanttFetchRef = useRef(false);
    const loadedProjectRef = useRef(null);

    const selectedProject = useMemo(
        () => projects.find((p) => String(p.project_id) === String(projectId)),
        [projects, projectId]
    );

    const assigneeOptions = useMemo(() => {
        const map = new Map();
        tasks.forEach((task) => {
            const list = task.assignees?.length
                ? task.assignees
                : task.assignee
                  ? [task.assignee]
                  : [];
            list.forEach((a) => {
                if (a?.user_id && isActiveTeamMember(a)) {
                    map.set(a.user_id, `${a.first_name || ''} ${a.last_name || ''}`.trim());
                }
            });
        });
        return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }, [tasks]);

    const reloadTimeline = async (focusUserId = previewUserId) => {
        if (!accessToken) return;
        setRefreshing(true);
        try {
            const params = {};
            if (projectId) params.project_id = projectId;
            // Only filter by user when the user explicitly selects someone — never auto-narrow.
            if (focusUserId) params.user_id = Number(focusUserId);
            const response = await axios.get(`${config.apiBASEURL}/TaskBoardRoutes/gantt/admin`, {
                headers: { Authorization: `Bearer ${accessToken}` },
                params,
            });
            setTasks(response.data?.tasks || []);
            setProjectName(
                response.data?.project_name ||
                    selectedProject?.project_name ||
                    (projectId ? '' : 'All projects')
            );
            loadedProjectRef.current = `${projectId || '__all__'}::${focusUserId || '__all_users__'}`;
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        if (!accessToken || !userId) return;

        const loadProjects = async () => {
            setProjectsLoading(true);
            setError('');
            try {
                let list = [];

                try {
                    const response = await axios.get(
                        `${config.apiBASEURL}/TaskBoardRoutes/gantt/projects`,
                        {
                            headers: { Authorization: `Bearer ${accessToken}` },
                            params: { user_id: userId },
                        }
                    );
                    if (Array.isArray(response.data?.projects)) {
                        list = response.data.projects;
                    }
                } catch (ganttProjectsErr) {
                    console.warn('gantt/projects unavailable, using boards fallback', ganttProjectsErr);
                }

                if (!list.length) {
                    const boardsRes = await axios.get(
                        `${config.apiBASEURL}/TaskBoardRoutes/fetch-boards/${userId}`,
                        { headers: { Authorization: `Bearer ${accessToken}` } }
                    );
                    list = boardsToProjects(boardsRes.data?.boards || []);
                }

                setProjects(list);

                const fromUrl = searchParams.get('project_id');
                if (fromUrl && list.some((p) => String(p.project_id) === String(fromUrl))) {
                    setProjectId(fromUrl);
                }
                // else leave empty = All projects (default)
            } catch (err) {
                console.error('Error loading gantt projects:', err);
                setError('Could not load projects for timeline.');
            } finally {
                setProjectsLoading(false);
            }
        };

        loadProjects();
    }, [accessToken, userId, searchParams]);

    useEffect(() => {
        if (!accessToken) {
            setTasks([]);
            setProjectName('');
            loadedProjectRef.current = null;
            return;
        }

        if (skipNextGanttFetchRef.current) {
            skipNextGanttFetchRef.current = false;
            return;
        }

        const scopeKey = `${projectId || '__all__'}::${previewUserId || '__all_users__'}`;
        const soft = loadedProjectRef.current === scopeKey;

        const fetchGantt = async () => {
            if (soft) setRefreshing(true);
            else {
                setLoading(true);
                setError('');
                setPreview(null);
            }
            try {
                const params = {};
                if (projectId) params.project_id = projectId;
                if (previewUserId) params.user_id = Number(previewUserId);
                const url = `${config.apiBASEURL}/TaskBoardRoutes/gantt/admin`;
                if (process.env.NODE_ENV !== 'production') {
                    // eslint-disable-next-line no-console
                    console.debug('[gantt] GET', url, params);
                }
                const response = await axios.get(url, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                    params,
                });
                setTasks(response.data?.tasks || []);
                setProjectName(
                    response.data?.project_name ||
                        selectedProject?.project_name ||
                        (projectId ? '' : 'All projects')
                );
                if (projectId) {
                    setSearchParams({ project_id: projectId });
                } else {
                    setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        next.delete('project_id');
                        return next;
                    });
                }
                loadedProjectRef.current = scopeKey;
            } catch (err) {
                console.error('Error loading admin gantt:', err);
                if (!soft) {
                    setError('Could not load project timeline.');
                    setTasks([]);
                }
            } finally {
                if (soft) setRefreshing(false);
                else setLoading(false);
            }
        };

        fetchGantt();
    }, [projectId, previewUserId, accessToken, setSearchParams, selectedProject?.project_name]);

    useEffect(() => {
        if (!previewUserId || !accessToken) {
            setConflicts([]);
            setPriorityWarnings([]);
            return;
        }

        const loadConflicts = async () => {
            setConflictsLoading(true);
            try {
                const response = await axios.get(
                    `${config.apiBASEURL}/TaskBoardRoutes/gantt/conflicts/${previewUserId}`,
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );
                setConflicts(response.data?.conflicts || []);
                setPriorityWarnings(response.data?.priority_warnings || []);
            } catch {
                setConflicts([]);
                setPriorityWarnings([]);
            } finally {
                setConflictsLoading(false);
            }
        };

        loadConflicts();
    }, [previewUserId, accessToken, tasks]);

    const previewMap = useMemo(() => {
        if (!preview?.changes) return new Map();
        return new Map(preview.changes.map((c) => [c.card_id, c.after?.due_date]));
    }, [preview]);

    const { rangeStart, rangeEnd } = useTeamStyleTimeline(tasks, 1, previewMap);

    const { memberRows, unassignedTasks } = useMemo(() => {
        const { members, unassigned } = groupTasksByAssignee(tasks);
        return { memberRows: members, unassignedTasks: unassigned };
    }, [tasks]);

    const pinFocusUserId = (task) => {
        if (task?.gantt_user_id) return Number(task.gantt_user_id);
        if (previewUserId) return Number(previewUserId);
        const a = task.assignees?.find((x) => isActiveTeamMember(x)) || task.assignees?.[0];
        return a?.user_id ? Number(a.user_id) : null;
    };

    const savePinStart = async (task, preferredStartAt, allocatedHours = null) => {
        const uid = pinFocusUserId(task);
        if (!uid || !accessToken || !task?.id) {
            setPinMessage('Select an assignee (or assign the card) before pinning a start time.');
            return false;
        }
        if (preferredStartAt) {
            setTasks((prev) =>
                applyOptimisticPinToTasks(prev, task.id, preferredStartAt, uid, allocatedHours, false)
            );
        }
        setPinSaving(true);
        setPinMessage('');
        try {
            const pinRes = await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/schedule/pin-start`,
                {
                    user_id: uid,
                    card_id: Number(task.id),
                    preferred_start_at: preferredStartAt,
                    allow_outside_window: true,
                    bump_others: false,
                    ...(allocatedHours != null ? { allocated_hours: allocatedHours } : {}),
                },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            setPinMessage(
                pinRes.data?.message ||
                    (allocatedHours != null
                        ? `Updated “${task.title}” duration to ${allocatedHours}h.`
                        : `Pinned “${task.title}”.`)
            );
            return true;
        } catch (err) {
            setPinMessage(err.response?.data?.message || 'Could not pin start time.');
            await reloadTimeline(previewUserId || '');
            return false;
        } finally {
            setPinSaving(false);
        }
    };

    const handlePreview = async () => {
        if (!previewUserId || !accessToken) return;
        setPreviewLoading(true);
        setApplyMessage('');
        try {
            const response = await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/schedule/preview`,
                { user_id: Number(previewUserId) },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            setPreview(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Preview failed');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleApply = async () => {
        const changes = preview?.impact_summary?.changes || preview?.changes || [];
        if (!changes.length || !accessToken) return;
        setApplyLoading(true);
        setApplyMessage('');
        try {
            const changesPayload = changes.map((c) => ({
                card_id: c.card_id,
                due_date: c.after.due_date,
            }));
            const response = await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/schedule/apply`,
                { changes: changesPayload, user_id: userId },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            setApplyMessage(response.data?.message || 'Schedule applied');
            setPreview(null);
            await reloadTimeline();
        } catch (err) {
            setApplyMessage(err.response?.data?.message || 'Apply failed');
        } finally {
            setApplyLoading(false);
        }
    };

    const showTimeline = !error && (!loading || tasks.length > 0);

    return (
        <Row className="body_content user-gantt-page team-planner-page">
            <Col lg={12} className="mb-2">
                <Breadcrumb className="mb-0">
                    <Breadcrumb.Item onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
                        <i className="pi pi-angle-left" /> Back
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>Project Timeline</Breadcrumb.Item>
                </Breadcrumb>
            </Col>

            <Col lg={12}>
                <Card>
                    <Card.Body className="user-gantt-card-body">
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <div className="d-flex align-items-center gap-1">
                                <Form.Label className="mb-0 me-1 fw-semibold small">Project</Form.Label>
                                {projectsLoading ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    <Form.Select
                                        size="sm"
                                        style={{ minWidth: 220, maxWidth: 360 }}
                                        value={projectId}
                                        onChange={(e) => {
                                            setProjectId(e.target.value);
                                            setPreviewUserId('');
                                            setPreview(null);
                                            setApplyMessage('');
                                        }}
                                    >
                                        <option value="">All projects</option>
                                        {projects.map((p) => (
                                            <option key={p.project_id} value={p.project_id}>
                                                {p.project_name}
                                                {p.brand_name ? ` · ${p.brand_name}` : ''}
                                                {` (${p.active_card_count} card${p.active_card_count === 1 ? '' : 's'})`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                )}
                            </div>

                            {showTimeline && (
                                <div className="d-flex align-items-center gap-1">
                                    <Form.Label className="mb-0 me-1 fw-semibold small">
                                        User
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        style={{ minWidth: 160, maxWidth: 220 }}
                                        value={previewUserId}
                                        onChange={(e) => {
                                            setPreviewUserId(e.target.value);
                                            setPreview(null);
                                            setApplyMessage('');
                                        }}
                                    >
                                        <option value="">All users</option>
                                        {assigneeOptions.map((opt) => (
                                            <option key={opt.id} value={opt.id}>
                                                {opt.name || `User ${opt.id}`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        disabled={!previewUserId || previewLoading}
                                        onClick={handlePreview}
                                    >
                                        {previewLoading ? 'Previewing…' : 'Preview'}
                                    </Button>
                                    {preview?.impact_summary && (
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            disabled={applyLoading}
                                            onClick={handleApply}
                                        >
                                            {applyLoading
                                                ? 'Applying…'
                                                : `Apply (${preview.impact_summary.tasks_shifting})`}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>

                        {pinMessage && (
                            <Alert
                                variant={
                                    /could not|select an assignee|denied|error/i.test(pinMessage)
                                        ? 'danger'
                                        : 'info'
                                }
                                className="py-1 px-2 small mb-2"
                            >
                                {pinMessage}
                            </Alert>
                        )}

                        {!projectsLoading && projects.length === 0 && (
                            <Alert variant="warning" className="py-2 small mb-2">
                                No projects with linked boards found for your account. Create a
                                project from Taskboard so boards get a project assigned.
                            </Alert>
                        )}

                        {selectedProject && (
                            <p className="text-muted small mb-2">
                                {selectedProject.board_count} board
                                {selectedProject.board_count === 1 ? '' : 's'}:{' '}
                                {selectedProject.boards.map((b) => b.name).join(', ')}
                            </p>
                        )}

                        {preview?.impact_summary && preview.impact_summary.tasks_shifting > 0 && (
                            <Alert variant="info" className="py-2 small">
                                <strong>Impact summary — {preview.impact_summary.message}</strong>
                                <p className="mb-2 mt-1 text-muted">
                                    {preview.impact_summary.tasks_shifting} task
                                    {preview.impact_summary.tasks_shifting === 1 ? '' : 's'} will move on the
                                    timeline for the selected assignee.
                                </p>
                                <table className="user-gantt-impact-table">
                                    <thead>
                                        <tr>
                                            <th>Task</th>
                                            <th>Before</th>
                                            <th>After</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.impact_summary.changes.map((c) => (
                                            <tr key={c.card_id}>
                                                <td>{c.title}</td>
                                                <td>
                                                    {c.before?.due_date
                                                        ? formatGanttDateTime(c.before.due_date)
                                                        : '—'}
                                                </td>
                                                <td>{formatGanttDateTime(c.after.due_date)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <small className="text-muted d-block mt-2">
                                    Solid bar = current due date · Striped = proposed shift
                                </small>
                            </Alert>
                        )}

                        {preview?.impact_summary?.tasks_shifting === 0 && (
                            <Alert variant="secondary" className="py-2 small">
                                {preview.impact_summary.message}
                            </Alert>
                        )}

                        {previewUserId && (conflicts.length > 0 || priorityWarnings.length > 0) && (
                            <ScheduleConflictModal
                                conflicts={conflicts}
                                priorityWarnings={priorityWarnings}
                                showProceed={false}
                                onRequestApproval={async (items) => {
                                    await axios.post(
                                        `${config.apiBASEURL}/TaskBoardRoutes/approvals`,
                                        {
                                            request_type: 'schedule_shift',
                                            user_id: Number(previewUserId),
                                            project_id: Number(projectId),
                                            reason: items.map((i) => i.message).join('; '),
                                            payload: {
                                                conflicts: items,
                                                changes: preview?.impact_summary?.changes || preview?.changes,
                                            },
                                        },
                                        { headers: { Authorization: `Bearer ${accessToken}` } }
                                    );
                                    setApplyMessage('Approval request submitted for schedule shift.');
                                }}
                            />
                        )}

                        {previewUserId && conflictsLoading && (
                            <p className="small text-muted">Checking assignee conflicts…</p>
                        )}

                        {applyMessage && (
                            <Alert variant="success" className="py-2 small">
                                {applyMessage}
                            </Alert>
                        )}

                        {loading && !tasks.length && (
                            <div className="text-center py-5">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading project timeline…
                            </div>
                        )}

                        {!loading && error && <p className="text-danger">{error}</p>}

                        {!loading && !error && tasks.length === 0 && (
                            <Alert variant="secondary" className="mb-0">
                                {projectId
                                    ? 'This project has boards but no active cards on the timeline (cards may be completed, on hold, or not yet created).'
                                    : 'No active cards found across projects on the timeline.'}
                            </Alert>
                        )}

                        {showTimeline && tasks.length > 0 && (
                            <TeamStyleGanttChart
                                title="Project timeline"
                                subtitle={`${projectName || 'All projects'} · ${formatTimelineScopeRange(
                                    rangeStart,
                                    rangeEnd
                                )}`}
                                activeCount={tasks.length}
                                members={memberRows}
                                onDateChange={async (ganttTask) => {
                                    const source = ganttTask?.sourceTask;
                                    if (!source) return false;
                                    const start =
                                        ganttTask.start instanceof Date
                                            ? ganttTask.start.toISOString()
                                            : ganttTask.start;
                                    const hours = ganttTask.isResize ? ganttTask.hours : null;
                                    return savePinStart(source, start, hours);
                                }}
                                defaultView={ViewMode.Day}
                                deferCommit={false}
                                sidePanel={
                                    unassignedTasks.length > 0 ? (
                                        <aside className="team-unassigned-pool">
                                            <div className="team-unassigned-header">
                                                Unassigned
                                                <Badge bg="secondary" className="ms-2">
                                                    {unassignedTasks.length}
                                                </Badge>
                                            </div>
                                            <p className="team-unassigned-hint">
                                                Assign these on the board to place them on a person
                                                row.
                                            </p>
                                            <ul className="team-unassigned-list">
                                                {unassignedTasks.map((task) => (
                                                    <li
                                                        key={task.id}
                                                        className="team-unassigned-card"
                                                        onClick={() =>
                                                            navigate(
                                                                `/Graphe/dashboard/kanbanboard/${task.board_id}?card=${task.id}`
                                                            )
                                                        }
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <div className="team-unassigned-title">
                                                            {task.title}
                                                        </div>
                                                        <div className="team-unassigned-meta">
                                                            {formatTaskContext(task) ||
                                                                task.board_name ||
                                                                'Board'}
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </aside>
                                    ) : null
                                }
                                previewMap={previewMap}
                                onBarClick={(t) => {
                                    if (t?.board_id && t?.id) {
                                        navigate(
                                            `/Graphe/dashboard/kanbanboard/${t.board_id}?card=${t.id}`
                                        );
                                    }
                                }}
                                refreshing={refreshing || pinSaving}
                                emptyMessage={
                                    unassignedTasks.length > 0
                                        ? 'All cards in this view are unassigned.'
                                        : 'No people with tasks in this view.'
                                }
                            />
                        )}

                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default AdminGantt;
